<template>
    <div>
        <div v-if="users.length > 1" class="py-0 pt-6 w-full max-w-9xl mx-auto">
            <div class="flex justify-between items-center">
                <ul class="pill-list mb-3">
                    <li v-for="(user, uIndex) in users" :key="'ukey-' + uIndex">
                        <a
                            class="btn-base pill"
                            href="#"
                            role="button"
                            :class="{ dark: user.active }"
                            @click.prevent="toggleUserShown(user)"
                            :id="`hs-dashboard-user-${uIndex}`"
                        >
                            {{ user.name }}
                        </a>
                    </li>
                </ul>
            </div>
        </div>
        <div v-if="!isEditMode" class="py-0 w-full max-w-9xl mx-auto">
            <StartGuide />
        </div>

        <div class="py-0 w-full max-w-9xl mx-auto">
            <div
                class="flex px-6 pt-5 pb-6 gap-6 self-stretch rounded-lg bg-white dark:bg-slate-800 shadow dark:shadow-slate-900/50 overflow-hidden mt-6 z-0"
            >
                <dl
                    class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 w-full"
                >
                    <div class="md:border-r-2 border-gray-100 dark:border-slate-700">
                        <NumberOfCards />
                    </div>
                    <div class="lg:border-r-2 border-gray-100 dark:border-slate-700">
                        <NumberOfPoints />
                    </div>
                    <div class="md:border-r-2 border-gray-100 dark:border-slate-700">
                        <AnnualFees />
                    </div>
                    <div class="md:border-gray-100 dark:border-slate-700 md:border-0">
                        <FiveTwentyFour />
                    </div>
                </dl>
            </div>
        </div>

        <!-- 10xTravel+ upgrade promo for non-subscribers -->
        <div
            v-if="!page.props.userData?.is_subscribed"
            class="pt-6 w-full max-w-9xl mx-auto"
        >
            <UpgradeBanner />
        </div>

        <!-- Widgets area: draggable in edit mode, static in default mode -->
        <div class="pt-6 w-full max-w-9xl mx-auto">
            <div v-if="isEditMode" class="relative">
                <div
                    v-if="localVisibleWidgets.length === 0"
                    class="absolute inset-0 flex flex-col items-center justify-center pointer-events-none z-10 empty-state-overlay"
                >
                    <div
                        class="w-16 h-16 bg-slate-200 dark:bg-slate-700 rounded-full flex justify-center items-center mb-5"
                    >
                        <Icon
                            icon="grid-add"
                            :width="32"
                            :height="32"
                            svg-class="text-slate-500 dark:text-slate-400"
                        />
                    </div>
                    <p class="text-lg font-medium text-slate-800 dark:text-slate-100">
                        Drag widgets here
                    </p>
                    <p class="text-sm text-slate-500 dark:text-slate-400">
                        Move widgets to show them on dashboard
                    </p>
                </div>
                <draggable
                    v-model="localVisibleWidgets"
                    item-key="widget_key"
                    :filter="'.no-drag'"
                    :animation="200"
                    ghost-class="drag-ghost"
                    drag-class="drag-active"
                    :group="{ name: 'widgets' }"
                    @change="onVisibleChange"
                    class="grid grid-cols-12 gap-6 items-start"
                    :class="
                        localVisibleWidgets.length === 0
                            ? 'min-h-[200px] sm:min-h-[360px] border-2 border-dashed border-slate-300 dark:border-slate-700 rounded-lg'
                            : ''
                    "
                >
                    <template #item="{ element }">
                        <div
                            :class="widgetColClass(element.widget_key)"
                            class="relative edit-mode-widget visible-edit-widget rounded-lg bg-white dark:bg-slate-800 cursor-grab"
                        >
                            <div
                                class="pointer-events-none overflow-hidden rounded-lg"
                            >
                                <component
                                    :is="widgetComponentMap[element.widget_key]"
                                    v-bind="widgetProps(element.widget_key)"
                                />
                            </div>
                            <div
                                class="absolute inset-x-0 bottom-0 pointer-events-none bg-white/70 dark:bg-slate-900/60"
                                :style="{
                                    top: widgetsWithoutTitle.includes(
                                        element.widget_key
                                    )
                                        ? '0'
                                        : '3.5rem',
                                    zIndex: 40,
                                }"
                            ></div>
                            <div
                                class="absolute top-3 right-3 flex items-center gap-2"
                                style="z-index: 41"
                            >
                                <UiTooltip
                                    text="Hide in dashboard"
                                    placement="top"
                                >
                                    <template #trigger>
                                        <button
                                            class="no-drag flex items-center p-3 sm:p-2 rounded border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 shadow-sm dark:shadow-slate-900/50 text-slate-500 dark:text-slate-300 hover:text-slate-700 dark:hover:text-slate-100 hover:bg-slate-50 dark:hover:bg-slate-700 transition-colors cursor-pointer"
                                            @click="
                                                hideWidget(element.widget_key)
                                            "
                                        >
                                            <Icon
                                                icon="eye-on"
                                                :width="16"
                                                :height="16"
                                            />
                                        </button>
                                    </template>
                                </UiTooltip>
                                <UiTooltip text="Drag to move" placement="top">
                                    <template #trigger>
                                        <button
                                            class="drag-handle flex items-center p-3 sm:p-2 rounded border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 shadow-sm dark:shadow-slate-900/50 text-slate-500 dark:text-slate-300 hover:text-slate-700 dark:hover:text-slate-100 hover:bg-slate-50 dark:hover:bg-slate-700 cursor-grab transition-colors"
                                        >
                                            <Icon
                                                icon="drag-handle"
                                                :width="16"
                                                :height="16"
                                            />
                                        </button>
                                    </template>
                                </UiTooltip>
                            </div>
                        </div>
                    </template>
                </draggable>
            </div>

            <div
                v-else-if="sortedVisibleWidgets.length > 0"
                class="grid grid-cols-12 gap-6"
            >
                <div
                    v-for="widget in sortedVisibleWidgets"
                    :key="widget.widget_key"
                    :class="widgetColClass(widget.widget_key)"
                    class="overflow-hidden rounded-lg [&>*]:h-full"
                >
                    <component
                        :is="widgetComponentMap[widget.widget_key]"
                        v-bind="widgetProps(widget.widget_key)"
                    />
                </div>
            </div>

            <EmptyState
                v-else
                action-label="+ Add widgets"
                container-styles="py-20"
                @action="onAddWidgets"
            >
                <template #image>
                    <div
                        class="w-16 h-16 bg-slate-200 dark:bg-slate-700 rounded-full flex justify-center items-center mb-5"
                    >
                        <Icon
                            icon="grid-add"
                            :width="32"
                            :height="32"
                            svg-class="text-slate-500 dark:text-slate-400"
                        />
                    </div>
                </template>
                <template #content>
                    <h5 class="text-slate-800 dark:text-slate-100 text-lg font-medium text-center">
                        No widgets here
                    </h5>
                    <p class="text-slate-500 dark:text-slate-400 text-sm mb-5 text-center">
                        Customize your dashboard by showing the widgets you need
                    </p>
                </template>
            </EmptyState>
        </div>

        <!-- Mobile: Customize widgets button (only on mobile, hidden on lg+) -->
        <div
            v-if="!isEditMode"
            class="pt-6 w-full max-w-9xl mx-auto flex justify-center lg:hidden"
        >
            <button
                type="button"
                class="flex items-center justify-center gap-2 px-4 py-2.5 text-sm font-medium leading-5 text-slate-800 dark:text-slate-100 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded shadow-sm"
                @click="showMobileCustomize = true"
            >
                <Icon icon="grid-add" :width="16" :height="16" />
                Customize widgets
            </button>
        </div>
    </div>

    <!-- Mobile customize modal -->
    <DashboardMobileCustomize
        :show="showMobileCustomize"
        :available-widget-keys="availableWidgetKeys"
        @close="showMobileCustomize = false"
    />
    <div v-if="isEditMode" class="pt-6 w-full max-w-9xl mx-auto">
        <div class="flex items-center gap-2 mb-4">
            <Icon
                icon="eye-off"
                :width="20"
                :height="20"
                svg-class="text-slate-800 dark:text-slate-100"
            />
            <h3 class="text-base font-semibold text-slate-800 dark:text-slate-100">
                Hidden widgets
            </h3>
        </div>

        <div class="relative">
            <div
                v-if="localHiddenWidgets.length === 0"
                class="absolute inset-0 flex flex-col items-center justify-center pointer-events-none z-10 empty-state-overlay"
            >
                <div
                    class="w-16 h-16 bg-slate-200 dark:bg-slate-700 rounded-full flex justify-center items-center mb-5"
                >
                    <Icon
                        icon="grid-add"
                        :width="32"
                        :height="32"
                        svg-class="text-slate-500 dark:text-slate-400"
                    />
                </div>
                <p class="text-lg font-medium text-slate-800 dark:text-slate-100">
                    No hidden widgets
                </p>
                <p class="text-sm text-slate-500 dark:text-slate-400">
                    Move widgets here to hide them
                </p>
            </div>
            <draggable
                v-model="localHiddenWidgets"
                item-key="widget_key"
                :filter="'.no-drag'"
                :animation="200"
                ghost-class="drag-ghost"
                drag-class="drag-active"
                :group="{ name: 'widgets' }"
                @change="onHiddenChange"
                class="grid grid-cols-12 gap-6 min-h-[200px] sm:min-h-[360px] rounded-lg hidden-drop-zone"
                :class="
                    localHiddenWidgets.length === 0
                        ? 'border-2 border-dashed border-slate-300 dark:border-slate-700 py-16'
                        : ''
                "
            >
                <template #item="{ element }">
                    <div
                        :class="widgetColClass(element.widget_key)"
                        class="relative edit-mode-widget hidden-edit-widget rounded-lg cursor-grab bg-slate-50 dark:bg-slate-900 shadow-[0_9px_25px_-5px_rgba(0,0,0,0.1),0_3px_10px_-5px_rgba(0,0,0,0.04)] dark:shadow-slate-950/60"
                    >
                        <div
                            class="pointer-events-none overflow-hidden rounded-lg"
                        >
                            <component
                                :is="widgetComponentMap[element.widget_key]"
                                v-bind="widgetProps(element.widget_key)"
                            />
                        </div>
                        <div
                            class="absolute inset-x-0 bottom-0 pointer-events-none bg-slate-50/70 dark:bg-slate-900/70"
                            :style="{
                                top: widgetsWithoutTitle.includes(
                                    element.widget_key
                                )
                                    ? '0'
                                    : '3.5rem',
                                zIndex: 40,
                            }"
                        ></div>
                        <div
                            class="absolute top-3 right-3 flex items-center gap-2"
                            style="z-index: 41"
                        >
                            <UiTooltip text="Show in dashboard" placement="top">
                                <template #trigger>
                                    <button
                                        class="no-drag flex items-center p-3 sm:p-2 rounded border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 shadow-sm dark:shadow-slate-900/50 text-slate-500 dark:text-slate-300 hover:text-slate-700 dark:hover:text-slate-100 hover:bg-slate-50 dark:hover:bg-slate-700 transition-colors cursor-pointer"
                                        @click="
                                            restoreWidget(element.widget_key)
                                        "
                                    >
                                        <Icon
                                            icon="eye-off"
                                            :width="16"
                                            :height="16"
                                        />
                                    </button>
                                </template>
                            </UiTooltip>
                            <UiTooltip text="Drag to move" placement="top">
                                <template #trigger>
                                    <button
                                        class="drag-handle flex items-center p-3 sm:p-2 rounded border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 shadow-sm dark:shadow-slate-900/50 text-slate-500 dark:text-slate-300 hover:text-slate-700 dark:hover:text-slate-100 hover:bg-slate-50 dark:hover:bg-slate-700 cursor-grab transition-colors"
                                    >
                                        <Icon
                                            icon="drag-handle"
                                            :width="16"
                                            :height="16"
                                        />
                                    </button>
                                </template>
                            </UiTooltip>
                        </div>
                    </div>
                </template>
            </draggable>
        </div>
    </div>
</template>
<script>
import { usePage } from '@inertiajs/vue3';
import { mapState, mapActions } from 'pinia';
import { useDashboardStore } from '@/store/dashboard';
import { useCreditScoreStore } from '@/store/creditscore';
import { useCompanionPassStore } from '@/store/companionpass';
import { useAppStore } from '@/store/app';
import { useCoursesStore } from '@/store/courses';
import { generateUUID } from '@/utils/uuid.js';
import { notify } from 'notiwind';
import draggable from 'vuedraggable';
import FiveTwentyFour from '@/components/dashboard/cards/five-twenty-four.vue';
import NumberOfCards from '@/components/dashboard/cards/number-of-cards.vue';
import NumberOfPoints from '@/components/dashboard/cards/number-of-points.vue';
import AnnualFees from '@/components/dashboard/cards/annual-fees.vue';
import CreditScore from '@/components/dashboard/cards/credit-score.vue';
import TopCard from '@/components/dashboard/cards/top-card.vue';
import SwCompanionPass from '@/components/dashboard/cards/sw-companion-pass.vue';
import StartGuide from '@/components/dashboard/cards/start-guide.vue';
import TxCourses from '@/components/dashboard/cards/tx-courses.vue';
import CourseProgress from '@/components/dashboard/cards/course-progress.vue';
import CourseFinish from '@/components/dashboard/cards/course-finish.vue';
import CompanionPassSection from '@/components/companion-pass/companion-pass-section.vue';
import MinimumSpendDeadlines from '@/components/dashboard/cards/minimum-spend-deadlines.vue';
import TravelGoals from '@/components/dashboard/cards/travel-goals.vue';
import CardBenefitsMonthly from '@/components/dashboard/cards/card-benefits-monthly.vue';
import CardBenefitsExpiring from '@/components/dashboard/cards/card-benefits-expiring.vue';
import BestCardsWidget from '@/components/dashboard/cards/best-cards-widget.vue';
import UpgradeBanner from '@/components/dashboard/cards/upgrade-banner.vue';
import Icon from '@/components/ui/icon/icon.vue';
import UiTooltip from '@/components/ui/tooltip/ui-tooltip.vue';
import EmptyState from '@/components/ui/empty-state/empty-state.vue';
import DashboardMobileCustomize from '@/components/dashboard/dashboard-mobile-customize.vue';

export default {
    name: 'Dashboard',
    props: [],
    components: {
        draggable,
        TxCourses,
        FiveTwentyFour,
        NumberOfCards,
        NumberOfPoints,
        AnnualFees,
        CreditScore,
        TopCard,
        SwCompanionPass,
        StartGuide,
        CourseProgress,
        CourseFinish,
        CompanionPassSection,
        MinimumSpendDeadlines,
        TravelGoals,
        CardBenefitsMonthly,
        CardBenefitsExpiring,
        BestCardsWidget,
        UpgradeBanner,
        Icon,
        UiTooltip,
        EmptyState,
        DashboardMobileCustomize,
    },
    computed: {
        ...mapState(useDashboardStore, [
            'users',
            'user',
            'walletId',
            'isEditMode',
            'widgetPreferences',
        ]),
        ...mapState(useAppStore, ['userSettings']),
        ...mapState(useCompanionPassStore, ['passEnabled', 'setPassEnabled']),
        courseProgress() {
            return useCoursesStore().progress;
        },
        page() {
            return usePage();
        },
        isFreeUser() {
            return !this.page.props.userData?.is_subscribed;
        },
        widgetComponentMap() {
            return {
                my_next_card: TopCard,
                credit_score: CreditScore,
                courses: this.courseProgress === 0 ? TxCourses : CourseProgress,
                minimum_spend_deadlines: MinimumSpendDeadlines,
                travel_goals: TravelGoals,
                card_benefits_monthly: this.isFreeUser
                    ? null
                    : CardBenefitsMonthly,
                card_benefits_expiring: this.isFreeUser
                    ? null
                    : CardBenefitsExpiring,
                companion_pass: this.passEnabled
                    ? SwCompanionPass
                    : CompanionPassSection,
                best_cards_by_category: this.isFreeUser
                    ? null
                    : BestCardsWidget,
            };
        },
        sortedVisibleWidgets() {
            return [...this.widgetPreferences]
                .filter((w) => {
                    if (!w.visible) return false;
                    if (!this.widgetComponentMap[w.widget_key]) return false;
                    return true;
                })
                .sort((a, b) => a.position - b.position);
        },
        hiddenWidgets() {
            return [...this.widgetPreferences]
                .filter(
                    (w) => !w.visible && this.widgetComponentMap[w.widget_key]
                )
                .sort((a, b) => a.position - b.position);
        },
        widgetLabels() {
            return {
                my_next_card: 'My next card',
                credit_score: 'Credit score',
                courses: '10x Course',
                minimum_spend_deadlines: 'Minimum spend deadlines',
                travel_goals: 'Travel goals',
                card_benefits_monthly: 'My benefits',
                card_benefits_expiring: 'Benefits expiring soon',
                companion_pass: 'Companion pass',
                best_cards_by_category: 'My best cards by category',
            };
        },
        widgetsWithoutTitle() {
            const keys = [];
            if (this.courseProgress === 0) keys.push('courses');
            if (!this.passEnabled) keys.push('companion_pass');
            return keys;
        },
        availableWidgetKeys() {
            return Object.keys(this.widgetComponentMap).filter(
                (key) => this.widgetComponentMap[key] !== null
            );
        },
    },
    data: () => ({
        showTxCourses: true,
        showMobileCustomize: false,
        localVisibleWidgets: [],
        localHiddenWidgets: [],
    }),
    methods: {
        ...mapActions(useCreditScoreStore, ['getUserScore', 'setActiveUser']),
        ...mapActions(useDashboardStore, [
            'getDashboardData',
            'setUser',
            'getTopCard',
            'setWalletId',
            'getMinSpendDeadlines',
            'refreshCardCounts',
            'updateWidgetPositions',
            'toggleWidgetVisibility',
            'enterEditMode',
        ]),
        ...mapActions(useAppStore, ['setShowAffiliateModal']),
        toggleUserShown(user) {
            this.setUser(user);
            this.setActiveUser(user);
            this.getUserScore(user);
            this.getMinSpendDeadlines();
        },
        toggleCourse() {
            this.showTxCourses = !this.showTxCourses;
        },
        handleVisibilityChange() {
            if (document.visibilityState === 'visible') {
                this.refreshCardCounts();
            }
        },
        widgetColClass(widgetKey) {
            if (widgetKey === 'my_next_card') {
                return 'col-span-12';
            }
            return 'col-span-12 lg:col-span-6';
        },
        widgetProps(widgetKey) {
            if (widgetKey === 'credit_score') {
                return { onDashboard: true };
            }
            return {};
        },
        onAddWidgets() {
            if (window.innerWidth < 1024) {
                this.showMobileCustomize = true;
            } else {
                this.enterEditMode();
            }
        },
        hideWidget(widgetKey) {
            this.toggleWidgetVisibility(widgetKey);
            notify(
                {
                    group: 'info',
                    title: 'Widget hidden',
                    text: 'Hidden widgets move below visible ones.',
                },
                4000
            );
        },
        restoreWidget(widgetKey) {
            this.toggleWidgetVisibility(widgetKey);
        },
        onVisibleChange(evt) {
            if (evt.added) {
                // Widget dragged from hidden → visible
                const widget = this.widgetPreferences.find(
                    (w) => w.widget_key === evt.added.element.widget_key
                );
                if (widget) {
                    widget.visible = true;
                }
            }
            // Sync positions from local order
            this.localVisibleWidgets.forEach((w, i) => {
                const pref = this.widgetPreferences.find(
                    (p) => p.widget_key === w.widget_key
                );
                if (pref) {
                    pref.position = i;
                }
            });
        },
        onHiddenChange(evt) {
            if (evt.added) {
                // Widget dragged from visible → hidden
                const widget = this.widgetPreferences.find(
                    (w) => w.widget_key === evt.added.element.widget_key
                );
                if (widget) {
                    widget.visible = false;
                    notify(
                        {
                            group: 'info',
                            title: 'Widget hidden',
                            text: 'Hidden widgets move below visible ones.',
                        },
                        4000
                    );
                }
            }
            // Sync positions from local order
            this.localHiddenWidgets.forEach((w, i) => {
                const pref = this.widgetPreferences.find(
                    (p) => p.widget_key === w.widget_key
                );
                if (pref) {
                    pref.position = i;
                }
            });
        },
    },
    created() {},
    mounted() {
        const walletId = usePage().props.userData.wallet_id;
        this.setWalletId(walletId);
        this.getUserScore();
        this.getDashboardData();
        this.getTopCard();

        document.addEventListener(
            'visibilitychange',
            this.handleVisibilityChange
        );

        const urlParams = new URLSearchParams(window.location.search);
        if (urlParams.get('verified') === '1') {
            if (
                this.user &&
                this.user.hashed_email_sha256 &&
                this.user.uuid &&
                typeof fbq !== 'undefined'
            ) {
                const event_id = generateUUID();
                fbq(
                    'track',
                    'CompleteRegistration',
                    {
                        em: this.user.hashed_email_sha256,
                        external_id: this.user.uuid,
                    },
                    { eventID: event_id }
                );
                if (window.history.replaceState) {
                    const cleanURL =
                        window.location.pathname + window.location.hash;
                    window.history.replaceState({}, document.title, cleanURL);
                }
            }
        }
    },
    beforeUnmount() {
        document.removeEventListener(
            'visibilitychange',
            this.handleVisibilityChange
        );
    },
    watch: {
        sortedVisibleWidgets: {
            handler(val) {
                this.localVisibleWidgets = [...val];
            },
            immediate: true,
            deep: true,
        },
        hiddenWidgets: {
            handler(val) {
                this.localHiddenWidgets = [...val];
            },
            immediate: true,
            deep: true,
        },
        userSettings: {
            handler() {
                this.setPassEnabled(this.userSettings.companion_pass_enabled);
            },
        },
    },
};
</script>

<style scoped>
/* Enable touch drag on mobile */
.drag-handle {
    touch-action: none;
}

/* Hide navigation links in widget headers during edit mode */
.edit-mode-widget :deep(a),
.edit-mode-widget :deep(.link-base),
.edit-mode-widget :deep(.link-dark) {
    visibility: hidden !important;
}

/* Drag ghost: dashed placeholder where widget will land */
.drag-ghost {
    border: 2px dashed #94a3b8 !important;
    border-radius: 8px !important;
    background: rgba(226, 232, 240, 0.25) !important;
}

:where(.dark) .drag-ghost {
    border-color: #475569 !important;
    background: rgba(15, 23, 42, 0.5) !important;
}

.drag-ghost > * {
    visibility: hidden !important;
}

/* Drag active: the element being dragged */
.drag-active {
    opacity: 0.9 !important;
    box-shadow:
        0 20px 25px -5px rgba(0, 0, 0, 0.1),
        0 8px 10px -6px rgba(0, 0, 0, 0.1) !important;
    border-radius: 8px !important;
    cursor: grabbing !important;
}

/* Hide empty state overlay when a ghost is being dragged into any zone */
.relative:has(.drag-ghost) > .empty-state-overlay {
    display: none !important;
}

/* Hide container dashed border and reset min-height when ghost is inside */
.hidden-drop-zone:has(.drag-ghost) {
    border-color: transparent !important;
    min-height: auto !important;
    padding-top: 0 !important;
    padding-bottom: 0 !important;
}

/* Visible zone: hide its dashed border and reset min-height when ghost is present */
.grid:has(.drag-ghost).border-dashed {
    border-color: transparent !important;
    min-height: auto !important;
    padding-top: 0 !important;
    padding-bottom: 0 !important;
}

/* Edit mode widgets: force inner widget background to transparent so container background shows through */
.visible-edit-widget :deep(> .pointer-events-none > *),
.hidden-edit-widget :deep(> .pointer-events-none > *) {
    background-color: transparent !important;
}
</style>
